import { RedoOutlined, ThunderboltOutlined } from '@ant-design/icons'
import { Alert, Button, Collapse, Modal } from 'antd'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { VideoDirectorProposalBatchV1 } from '../../api'
import DirectorConceptCard from './DirectorConceptCard'
import { mapBatchToConcepts } from './mapDirectorProposals'
import { DEV_FIXTURE_DIRECTOR_CONCEPTS } from './videoDirectorFixtures'
import type { DirectorHarnessStatus, VideoDirectorConceptV1 } from './videoDirectorTypes'

/**
 * VIDEO_DIRECTOR_PROPOSAL UI — generate / history / select.
 * Real Skill runs only; never fabricates ChatGPT-shaped text locally.
 */
export default function DirectorConceptsPanel({
  concepts,
  batches,
  status,
  selectedConceptId,
  selectionStale,
  errorMessage,
  onSelect,
  onRequestGenerate,
  onRegenerate,
  disabledReason,
}: {
  concepts: VideoDirectorConceptV1[]
  batches: VideoDirectorProposalBatchV1[]
  status: DirectorHarnessStatus
  selectedConceptId: string | null
  selectionStale: boolean
  errorMessage: string | null
  onSelect: (conceptId: string, batchId: string) => void
  onRequestGenerate: () => void
  onRegenerate: () => void
  disabledReason: string | null
}) {
  const { t } = useTranslation(['video', 'actions'])
  const [regenConfirmOpen, setRegenConfirmOpen] = useState(false)
  const hasConcepts = concepts.length > 0
  const olderBatches = useMemo(() => batches.slice(1), [batches])

  return (
    <div className="tc-vdc-panel">
      <div className="tc-vdc-panel-head">
        <div className="tc-vdc-panel-title">{t('video:director.concepts.title')}</div>
        {hasConcepts && (
          <div className="tc-vdc-panel-sub">{t('video:director.concepts.generatedCount', { count: concepts.length })}</div>
        )}
      </div>

      {selectionStale && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('video:director.concepts.upstreamStaleTitle')}
          description={t('video:director.concepts.upstreamStaleDesc')}
        />
      )}

      {status === 'error' && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('video:director.concepts.generateFailed')}
          description={errorMessage || t('video:director.concepts.generateFailedDesc')}
          action={
            <Button size="small" onClick={onRequestGenerate}>
              {t('video:director.concepts.regenerate')}
            </Button>
          }
        />
      )}

      {!hasConcepts && status !== 'generating' && (
        <div className="tc-vdc-panel-empty">
          <ThunderboltOutlined className="tc-vdc-panel-empty-icon" />
          <div className="tc-vdc-panel-empty-desc">{t('video:director.concepts.emptyDesc')}</div>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={onRequestGenerate}
            disabled={!!disabledReason}
            title={disabledReason || undefined}
          >
            {t('video:director.concepts.generateButton')}
          </Button>
          {disabledReason && <div className="tc-vdc-panel-disabled-hint">{disabledReason}</div>}
        </div>
      )}

      {status === 'generating' && (
        <div className="tc-vdc-panel-empty">
          <ThunderboltOutlined className="tc-vdc-panel-empty-icon tc-vdc-spin" />
          <div className="tc-vdc-panel-empty-title">{t('video:director.concepts.generating')}</div>
        </div>
      )}

      {hasConcepts && (
        <>
          <div className="tc-vdc-concepts-list">
            {concepts.map((concept) => (
              <DirectorConceptCard
                key={`${concept.batch_id || 'latest'}-${concept.concept_id}`}
                concept={concept}
                selected={selectedConceptId === concept.concept_id && !selectionStale}
                selectDisabled={selectionStale || status === 'generating'}
                onSelect={() => onSelect(concept.concept_id, concept.batch_id || batches[0]?.batch_id || '')}
              />
            ))}
          </div>
          <div className="tc-vdc-panel-regen-row">
            <Button icon={<RedoOutlined />} onClick={() => setRegenConfirmOpen(true)} disabled={status === 'generating'}>
              {t('video:director.concepts.regenerateThree')}
            </Button>
          </div>
        </>
      )}

      {olderBatches.length > 0 && (
        <Collapse
          style={{ marginTop: 16 }}
          items={olderBatches.map((batch, idx) => ({
            key: batch.batch_id,
            label: t('video:director.concepts.historyBatch', {
              index: batches.length - idx - 1,
              date: batch.created_at || batch.batch_id,
            }),
            children: (
              <div className="tc-vdc-concepts-list">
                {mapBatchToConcepts(batch).map((concept) => (
                  <DirectorConceptCard
                    key={`${batch.batch_id}-${concept.concept_id}`}
                    concept={concept}
                    selected={selectedConceptId === concept.concept_id && !selectionStale}
                    selectDisabled={selectionStale || status === 'generating'}
                    onSelect={() => onSelect(concept.concept_id, batch.batch_id)}
                  />
                ))}
              </div>
            ),
          }))}
        />
      )}

      <Modal
        open={regenConfirmOpen}
        title={t('video:director.concepts.regenConfirmTitle')}
        okText={t('video:director.concepts.regenConfirmOk')}
        cancelText={t('actions:cancel')}
        onCancel={() => setRegenConfirmOpen(false)}
        onOk={() => {
          setRegenConfirmOpen(false)
          onRegenerate()
        }}
      >
        {t('video:director.concepts.regenConfirmContent')}
      </Modal>
    </div>
  )
}

/** DEV-only helper — never imported by production page routes. */
export function loadDevFixtureConcepts(): VideoDirectorConceptV1[] {
  if (!import.meta.env.DEV) return []
  return DEV_FIXTURE_DIRECTOR_CONCEPTS
}
